import { ref, onMounted } from "vue";
import { useAiTextStore } from "@/stores/aiTextStore";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useGeneralStore } from "@/stores/generalStore";
import {
  getText,
  getTranslation,
} from "@/composables/text-generator/useOpenAiApi";
import { useNotificationsStore } from "@/stores/notifications";
import { useKeywordsStore } from "@/stores/keywordsStore";
import IContext from "types/Ai/Text/IContext";
import { iso639 } from "@topol/utils";
import { useI18n } from "vue-i18n";

export default () => {
  const aiTextStore = useAiTextStore();
  const mjmlStore = useMjmlStore();
  const generalStore = useGeneralStore();
  const keywordsStore = useKeywordsStore();
  const { t } = useI18n();

  const context = ref<IContext | undefined>(undefined);

  const generateAiText = async (prompt: string) => {
    aiTextStore.loading = true;

    const text = await getText(
      prompt,
      mjmlStore.authorize.apiKey,
      mjmlStore.authorize.hostname,
      mjmlStore.authorize.userId,
      true
    );

    if (text === null) {
      aiTextStore.loading = false;
      return;
    }

    const aiText = aiTextStore.cleanString(text);

    if (aiTextStore.blockId !== null) {
      aiTextStore.addMessageToBlockContext(aiTextStore.blockId, prompt, aiText);
    } else {
      aiTextStore.addMessageToNewBlockContext(prompt, aiText);
    }

    getContext();

    aiTextStore.loading = false;
  };

  const getContext = () => {
    context.value = aiTextStore.getContextByBlockId(aiTextStore.blockId);
  };

  const useInTemplate = (message: string) => {
    if (aiTextStore.blockId) {
      mjmlStore.updateSpecificBlockProp({
        prop: "content",
        value: message,
        sectionIndex: aiTextStore.sectionIndex,
        columnIndex: aiTextStore.columnIndex,
        blockIndex: aiTextStore.blockIndex,
      });
    } else {
      mjmlStore.addBlock({
        sectionIndex: aiTextStore.sectionIndex,
        columnIndex: aiTextStore.columnIndex,
        blockIndex: aiTextStore.blockIndex,
        type: "text",
        customBlockData: null,
        defaultContent: message,
      });
    }
  };

  onMounted(function () {
    aiTextStore.clearContext();
    getContext();
  });

  const improve = async () => {
    await generateAiText(
      t("ai_prompts.improve") + ": " + aiTextStore.promptText + '"'
    );
  };

  const makeShorter = async () => {
    await generateAiText(
      t("ai_prompts.make_shorter") + ": " + aiTextStore.promptText + '"'
    );
  };

  const makeLonger = async () => {
    await generateAiText(
      t("ai_prompts.make_longer") + ": " + aiTextStore.promptText + '"'
    );
  };

  const rephrase = async () => {
    await generateAiText(
      t("ai_prompts.rephrase") + ": " + aiTextStore.promptText + '"'
    );
  };

  const getCurrentLanguageName = (): string | null => {
    if (
      generalStore.multilingual &&
      mjmlStore.mjml.langs &&
      mjmlStore.mjml.langs.length > 0
    ) {
      const langs = generalStore.customLanguagePreset || iso639;
      const found = langs.find((l) => l.code === mjmlStore.langToShow);
      return found?.name ?? null;
    }
    return null;
  };

  const checkGrammar = async () => {
    let prompt = t("ai_prompts.check_grammar");

    const langName = getCurrentLanguageName();
    if (langName) {
      prompt += ` (the text is written in ${langName})`;
    }

    await generateAiText(prompt + ': "' + aiTextStore.promptText + '"');
  };

  const translate = async (lang: string): Promise<string | null> => {
    aiTextStore.loading = true;

    const uid = "single-block";
    const result = await getTranslation(
      [{ uid, content: aiTextStore.promptText }],
      lang
    );

    aiTextStore.loading = false;

    if (!result) {
      return null;
    }

    useNotificationsStore().createNotification({
      type: "info",
      title: t("translation.completed"),
      text: t("translation.detected_language", {
        language: result.detectedLanguage,
      }),
    });

    const translated = result.translations.find((t) => t.uid === uid);
    return translated?.content ?? null;
  };

  const getKeywords = async () => {
    if (
      collectTextFromMjml() !== "" &&
      keywordsStore.longText !== collectTextFromMjml()
    ) {
      aiTextStore.loading = true;
      keywordsStore.loading = true;
      const prompt =
        t("ai_prompts.get_keywords") +
        ": " +
        collectTextFromMjml() +
        " keywords:";

      const keywords = await getText(
        prompt,
        mjmlStore.authorize.apiKey,
        mjmlStore.authorize.hostname,
        mjmlStore.authorize.userId
      );

      if (keywords === null) {
        aiTextStore.loading = false;
        keywordsStore.loading = false;
        return;
      }

      const keywordsArray = keywords.trim().split(",");

      keywordsStore.setKeywords(keywordsArray);
      keywordsStore.longText = collectTextFromMjml();

      aiTextStore.loading = false;
      keywordsStore.loading = false;

      return keywords.trim();
    } else {
      return "";
    }
  };

  function collectTextFromMjml() {
    const mjml = mjmlStore.getMjml();

    let text = "";

    for (const section of mjml.children[0].children) {
      for (const column of section.children) {
        for (const block of column.children) {
          if (block.tagName === "mj-text") {
            const tmp = document.createElement("DIV");
            tmp.innerHTML = block.content;
            text += tmp.textContent || tmp.innerText || "";
          }

          // possibly add product, article, button
        }
      }
    }

    text = text.trim();

    return text;
  }

  return {
    improve,
    makeLonger,
    makeShorter,
    rephrase,
    checkGrammar,
    translate,
    getKeywords,
    useInTemplate,
    collectTextFromMjml,
    context,
  };
};
